import { useState } from 'react'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Sparkles, Loader2, Copy, Check } from 'lucide-react'
import api from '@/services/api'
import { toast } from 'sonner'

interface AiAnalystModalProps {
  isOpen: boolean
  onClose: () => void
  tab: string
  period: string
  data: any
}

export function AiAnalystModal({ isOpen, onClose, tab, period, data }: AiAnalystModalProps) {
  const [loading, setLoading] = useState(false)
  const [analysis, setAnalysis] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  async function generateAnalysis() {
    setLoading(true)
    try {
      const resp = await api.post('/ai/analyze-dashboard', { tab, period, data })
      setAnalysis(resp.data.data)
    } catch (error: any) {
      toast.error('Falha ao gerar análise: ' + (error.response?.data?.message || error.message))
    } finally {
      setLoading(false)
    }
  }

  function copyToClipboard() {
    if (analysis) {
      navigator.clipboard.writeText(analysis)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
      toast.success('Análise copiada para a área de transferência')
    }
  }

  return (
    <Modal open={isOpen} onClose={onClose} title='Analista IA Agrada' size='lg'>
      <div className='space-y-4'>
        {!analysis && !loading && (
          <div className='flex flex-col items-center justify-center py-10 text-center'>
            <div className='w-16 h-16 rounded-full bg-[var(--brand-primary)]/10 flex items-center justify-center mb-4'>
              <Sparkles className='text-[var(--brand-primary)]' size={32} />
            </div>
            <h3 className='text-lg font-semibold text-text-primary mb-2'>Precisa de uma visão estratégica?</h3>
            <p className='text-sm text-text-muted max-w-sm mb-6'>
              Nossa inteligência artificial vai analisar os dados do seu dashboard (aba {tab}) para o período de {period} e gerar um relatório executivo com tendências e oportunidades.
            </p>
            <Button onClick={generateAnalysis} icon={<Sparkles size={16} />}>
              Gerar Análise Agora
            </Button>
          </div>
        )}

        {loading && (
          <div className='flex flex-col items-center justify-center py-20 text-center'>
            <Loader2 className='text-[var(--brand-primary)] animate-spin mb-4' size={40} />
            <p className='text-text-primary font-medium'>Analisando métricas...</p>
            <p className='text-xs text-text-muted mt-2 italic'>Processando funil, conversões e performance de equipe.</p>
          </div>
        )}

        {analysis && (
          <div className='animate-in fade-in slide-in-from-bottom-4 duration-500'>
            <div className='flex items-center justify-between mb-4'>
              <h4 className='text-sm font-semibold text-[var(--brand-primary)] flex items-center gap-2'>
                <Sparkles size={14} /> Relatório Gerado
              </h4>
              <Button variant='ghost' size='sm' onClick={copyToClipboard} icon={copied ? <Check size={14} /> : <Copy size={14} />}>
                {copied ? 'Copiado' : 'Copiar'}
              </Button>
            </div>
            <div className='text-sm text-text-primary glass-card p-6 border-l-4 border-l-[var(--brand-primary)] max-h-[60vh] overflow-y-auto custom-scrollbar' style={{ whiteSpace: 'pre-wrap' }}>
              {analysis}
            </div>
            <div className='mt-6 flex justify-end gap-3'>
              <Button variant='secondary' onClick={() => setAnalysis(null)}>Recomeçar</Button>
              <Button onClick={onClose}>Fechar</Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}
